import { Component, OnInit, Input, EventEmitter, Output, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { FormUtil, FormOutType } from 'src/app/utils/form.util';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { TypeForm } from 'src/app/utils/type-form';
import { FilterPageType } from './filter-settings/filter-settings.component';

export interface AoFilterHeaderSelect {
    value: any;
    name: string | null;
}

export interface AoFilterHeaderSettings {
    defaultLimit: number;
    fields: AoFilterHeaderField[];
}

export interface AoFilterHeaderField {
    placeholder: string;
    name: string;
    searchType: FormOutType;
    selectValues?: AoFilterHeaderSelect[];
    sortKey?: any;
    sortState?: 'desc' | 'asc';
    span?: number;
    locked?: boolean;
}

/**
 * Component for the header of a list. Every column can used for sorting and filtering.
 * A settings button allows hiding and revealing specific columns, resetting filters and saving the current filter settings.
 */
@Component({
    selector: 'app-ao-filter-header-list',
    templateUrl: './ao-filter-list-header.component.html',
    styleUrls: ['./ao-filter-list-header.component.scss'],
    standalone: false,
})
export class AoFilterListHeaderComponent implements OnInit, OnDestroy {
    /**
     * Whether the select all checkbox should be shown
     */
    @Input() showSelect = true;
    /**
     * Whether the filter settings button should be shown
     */
    @Input() showFilterSettings = false;
    /**
     * The filter settings
     */
    @Input() settings!: AoFilterHeaderSettings;
    /**
     * The ids of all documents
     */
    @Input() ids: string[] = [];

    private _allSelected = false;
    get allSelected() {
        return this._allSelected;
    }
    /**
     * Whether all documents are selected
     */
    @Input() set allSelected(value) {
        this._allSelected = value;
        this.onSelectAll();
    }

    /**
     * The ids of the seleccted documents
     */
    @Input() selected: string[] = [];
    /**
     * Emitted when the selection changed
     */
    @Output() selectedChange = new EventEmitter<string[]>();

    /**
     * The filter of the input
     */
    @Input() filter: any;
    /**
     * Emitted when the filter changed
     */
    @Output() filterChange = new EventEmitter<any>();

    /**
     * The number of documents to skip on the next request
     */
    @Input() skip = 0;
    /**
     * Emitted when the skip is changed
     */
    @Output() skipChange = new EventEmitter<number>();

    /**
     * The number of documents to request at a time
     */
    @Input() limit = 0;
    /**
     * Emitted when the limit is changed
     */
    @Output() limitChange = new EventEmitter<number>();

    /**
     * The sorting order of the selected column
     */
    @Input() sort!: { sortKey: any; sortType: 'desc' | 'asc' };

    /**
     * The page type
     */
    @Input() pageType?: FilterPageType;

    /**
     * Whether the checkbox should be aligned left (otherwise centered)
     */
    @Input() alignCheckboxLeft = false;

    /**
     * Emitted when the sorting order changed
     */
    @Output() sortChange = new EventEmitter<{ sortKey: any; sortType: 'desc' | 'asc' }>();

    /**
     * Emitted when all selected changed
     */
    @Output() allSelectedChange = new EventEmitter<boolean>();

    /**
     * Emitted when the hidden filters changed
     */
    @Output() hiddenFiltersChange = new EventEmitter<string[]>();

    form!: TypeForm;

    private formUtil!: FormUtil;
    private formSub!: Subscription;

    width = 'calc(100% - 0px)';
    spans = 0;
    visibleSpans = 0;

    public hiddenFilters: string[] = [];

    private readonly destroy = new Subject<void>();

    ngOnInit(): void {
        this.limit = this.settings.defaultLimit;

        if (this.showSelect && this.showFilterSettings) {
            this.width = 'calc(100% - 90px)';
        } else if (this.showSelect) {
            this.width = 'calc(100% - 50px)';
        } else if (this.showFilterSettings) {
            this.width = 'calc(100% - 50px)';
        } else {
            this.width = 'calc(100% - 0px)';
        }

        this.form = new TypeForm({});
        for (const field of this.settings.fields) {
            if (field.span) {
                this.spans += field.span - 1;
            }

            this.form.addControl(
                field.name,
                new UntypedFormControl(this.filter[field.name] ? this.filter[field.name] : ''),
            );
        }

        this.formUtil = new FormUtil(this.form);

        this.hiddenFiltersChange.pipe(takeUntil(this.destroy), startWith(this.hiddenFilters)).subscribe((hf) => {
            this.visibleSpans = this.settings.fields.reduce(
                (a, c) => (hf.some((f) => c.name === f) ? a : a + (c.span ?? 1)),
                0,
            );
        });

        this.subscribeChanges();
    }

    ngOnDestroy(): void {
        this.formSub.unsubscribe();
        this.destroy.next();
        this.destroy.complete();
    }

    //TODO
    // in evaluation component verwendet ist aber ggf. unnnötig (?)
    // später evtl. weg wenn man evaluation component neu macht
    setFormValues(): void {
        this.formSub.unsubscribe();
        setTimeout(() => {
            Object.keys(this.form.controls).forEach((key) => {
                this.form
                    .get(key)
                    ?.setValue(this.filter[key] ? this.filter[key] : '', { emitEvent: false, onlySelf: true });
            });

            setTimeout(() => {
                this.subscribeChanges();
            });
        });
    }

    subscribeChanges(): void {
        this.formSub = this.form.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
            this.changes();
        });
    }

    changes(): void {
        this.skip = 0;
        this.limit = this.settings.defaultLimit;
        this.skipChange.emit(this.skip);
        this.limitChange.emit(this.limit);
        this.filter = this.createFilterMap();
        this.filterChange.emit(this.filter);
    }

    get formOutType() {
        return FormOutType;
    }

    onSelectAll(): void {
        if (!this.allSelected) {
            this.selected = [];
        } else {
            this.selected = this.ids;
        }

        this.allSelectedChange.emit(this.allSelected);

        this.selectedChange.emit(this.selected);
    }

    onSort(field: AoFilterHeaderField): void {
        if (!field.sortState) {
            field.sortState = 'asc';
        } else if (field.sortState === 'asc') {
            field.sortState = 'desc';
        } else {
            field.sortState = undefined;
        }

        for (const fi of this.settings.fields) {
            if (fi.name !== field.name) {
                fi.sortState = undefined;
            }
        }

        if (field.sortState && field.sortKey) {
            this.sort = { sortKey: field.sortKey, sortType: field.sortState };
        }

        this.sortChange.emit(this.sort);
    }

    private createFilterMap(): any {
        const result: any = {};
        for (const field of this.settings.fields) {
            result[field.name] = this.formUtil.getValue({ key: field.name, outType: field.searchType });
        }

        return result;
    }

    filterSelected(filter?: any, emit = true): void {
        if (!filter) {
            this.form.reset({}, { emitEvent: false });
        } else {
            this.form.reset({}, { emitEvent: false });

            for (const field of this.settings.fields) {
                let value: any = '';
                if (
                    this.isEmpty(filter[field.name]) &&
                    field.selectValues?.length &&
                    field.searchType === FormOutType.STRING
                ) {
                    filter[field.name] = null;
                }

                if (!this.isEmpty(filter[field.name])) {
                    value = filter[field.name];
                }

                this.form.get(field.name)?.setValue(value, { emitEvent: false, onlySelf: true });
            }
        }

        this.filter = filter ?? {};

        if (emit) {
            this.filterChange.emit(this.filter);
        }
    }

    private isEmpty(value: any): boolean {
        return value === null || value === undefined || value === '';
    }
}
